import React, { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { Link, useLocation, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Window from "../../../../../elements/window";

function ListBrand(props) {
  const params = useParams();
  const location = useLocation();
  const [table, setTable] = useState({
    head: ["Group", "Brand", "No. of Outlets", "Actions"],
    data: false,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterGroup, setFilterGroup] = useState("all");
  const [sortBy, setSortBy] = useState("brand");
  const [sortOrder, setSortOrder] = useState("asc");
  const [load, setLoad] = useState(0); // 0: loading, 1: data loaded, 2: ready for export

  // Ref to track previous export state
  const prevExport = useRef(0);

  useEffect(() => {
    const fetchData = async () => {
      if (props.user && props.user.data) {
        setLoad(0); // Start loading
        try {
          const response = await axios.get(
            props.chimeraserv.urlGet + "/group",
            {
              headers: {
                Authorization: props.user.data.usrKey,
                "Content-Type": "application/json",
                Accept: "application/json",
              },
            }
          );
          let data = [];
          if (Object.keys(response.data).length) {
            data = response.data;
          }
          setTable((prev) => ({
            ...prev,
            data: data,
          }));
          setLoad(1); // Data loaded
        } catch (error) {
          setTable((prev) => ({
            ...prev,
            data: [],
          }));
          setLoad(1); // Even on error, mark as loaded
        }
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, [props.user, props.chimeraserv.urlGet]);

  // Set load to 2 when data is ready
  useEffect(() => {
    if (load === 1 && table.data !== false) {
      setLoad(2);
    }
  }, [load, table.data]);

  // Create flat brand data for filtering and searching
  const flatBrandData = useMemo(() => {
    if (!table.data) return [];

    const brands = [];
    table.data.forEach((group) => {
      group.brands.forEach((brand) => {
        brands.push({
          ...brand,
          groupName: group.name,
          groupId: group._id,
          groupKey: group.grpKey,
          group: group, // Keep reference to parent group
        });
      });
    });
    return brands;
  }, [table.data]);

  // Filter, search, and sort logic
  const filteredAndSortedData = useMemo(() => {
    if (!flatBrandData.length) return [];

    let filtered = flatBrandData.filter((brand) => {
      // Search filter
      const matchesSearch =
        searchTerm === "" ||
        brand.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        brand.groupName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        brand.braKey.toLowerCase().includes(searchTerm.toLowerCase());

      // Status filter
      const matchesStatus =
        filterStatus === "all" ||
        (filterStatus === "active" && brand.status === 1) ||
        (filterStatus === "inactive" && brand.status === 0);

      // Group filter
      const matchesGroup =
        filterGroup === "all" || brand.groupId === filterGroup;

      return matchesSearch && matchesStatus && matchesGroup;
    });

    // Sort data
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "brand":
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case "group":
          aValue = a.groupName.toLowerCase();
          bValue = b.groupName.toLowerCase();
          break;
        case "outlets":
          aValue = a.chains.length;
          bValue = b.chains.length;
          break;
        case "status":
          aValue = a.status;
          bValue = b.status;
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [flatBrandData, searchTerm, filterStatus, filterGroup, sortBy, sortOrder]);

  // Get unique groups for filter dropdown
  const availableGroups = useMemo(() => {
    if (!table.data) return [];
    return table.data.map((group) => ({
      id: group._id,
      name: group.name,
    }));
  }, [table.data]);

  // Component that receives props from Window component - handles export
  const BrandListContent = ({ export: exportTime, getData }) => {
    // Export logic
    useEffect(() => {
      if (prevExport.current !== exportTime && load === 2 && getData) {
        prevExport.current = exportTime;

        // Prepare export data based on filtered results
        const exportData = filteredAndSortedData.map((brand, index) => {
          const status = brand.status === 1 ? "Active" : "Inactive";

          return [
            (index + 1).toString(),
            brand.groupName,
            brand.name,
            brand.chains.length.toString(),
            status,
            // brand.braKey,
          ];
        });

        // Get summary statistics
        const totalBrands = filteredAndSortedData.length;
        const activeBrands = filteredAndSortedData.filter(
          (b) => b.status === 1
        ).length;
        const inactiveBrands = filteredAndSortedData.filter(
          (b) => b.status === 0
        ).length;
        const totalOutlets = filteredAndSortedData.reduce(
          (sum, b) => sum + b.chains.length,
          0
        );

        // Get unique groups count
        const uniqueGroups = [
          ...new Set(filteredAndSortedData.map((b) => b.groupId)),
        ].length;

        // Get outlet statistics
        const avgOutletsPerBrand =
          totalBrands > 0 ? (totalOutlets / totalBrands).toFixed(2) : 0;
        const maxOutlets =
          totalBrands > 0
            ? Math.max(...filteredAndSortedData.map((b) => b.chains.length))
            : 0;
        const minOutlets =
          totalBrands > 0
            ? Math.min(...filteredAndSortedData.map((b) => b.chains.length))
            : 0;

        // Create summary data
        const summaryData = [
          ["Total Brands", totalBrands.toString()],
          ["Active Brands", activeBrands.toString()],
          ["Inactive Brands", inactiveBrands.toString()],
          ["Total Outlets", totalOutlets.toString()],
          ["Unique Groups", uniqueGroups.toString()],
          ["Avg Outlets per Brand", avgOutletsPerBrand.toString()],
          ["Max Outlets (Brand)", maxOutlets.toString()],
          ["Min Outlets (Brand)", minOutlets.toString()],
          ...(searchTerm || filterStatus !== "all" || filterGroup !== "all"
            ? [
                ["Filtered Results", "Yes"],
                ...(searchTerm ? [["Search Term", searchTerm]] : []),
                ...(filterStatus !== "all"
                  ? [
                      [
                        "Status Filter",
                        filterStatus.charAt(0).toUpperCase() +
                          filterStatus.slice(1),
                      ],
                    ]
                  : []),
                ...(filterGroup !== "all"
                  ? [
                      [
                        "Group Filter",
                        availableGroups.find((g) => g.id === filterGroup)
                          ?.name || "Unknown",
                      ],
                    ]
                  : []),
                ...(sortBy !== "brand"
                  ? [
                      [
                        "Sorted By",
                        sortBy.charAt(0).toUpperCase() +
                          sortBy.slice(1) +
                          " (" +
                          sortOrder.toUpperCase() +
                          ")",
                      ],
                    ]
                  : []),
              ]
            : []),
        ];

        getData({
          type: "table",
          title: "Brand Management Report",
          body: [
            // Main brand data table
            {
              head: [
                [
                  "#",
                  "Group",
                  "Brand",
                  "No. of Outlets",
                  "Status",
                  // "Brand Key",
                ],
              ],
              body: exportData,

              sheetName: "Brands",
            },
            // Summary table
            {
              head: [["Summary", "Value"]],
              body: summaryData,
              sheetName: "Summary",
            },
          ],
          id: "exportPdf_BrandList",
        });
      }
      // eslint-disable-next-line
    }, [
      exportTime,
      load,
      filteredAndSortedData,
      searchTerm,
      filterStatus,
      filterGroup,
      sortBy,
      sortOrder,
      availableGroups,
      getData,
    ]);

    return (
      <div className="table-container">
        <table className="table table-striped defaultTable">
          <thead>
            <tr>
              <th
                scope="col"
                onClick={() => handleSort("group")}
                className="sortable"
              >
                Group
                <FontAwesomeIcon
                  className="sort-icon"
                  icon={["fas", getSortIcon("group")]}
                />
              </th>
              <th
                scope="col"
                onClick={() => handleSort("brand")}
                className="sortable"
              >
                Brand
                <FontAwesomeIcon
                  className="sort-icon"
                  icon={["fas", getSortIcon("brand")]}
                />
              </th>
              <th
                scope="col"
                onClick={() => handleSort("outlets")}
                className="sortable"
              >
                No. of Outlets
                <FontAwesomeIcon
                  className="sort-icon"
                  icon={["fas", getSortIcon("outlets")]}
                />
              </th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedData.length > 0 ? (
              renderData()
            ) : (
              <tr>
                <td
                  colSpan={table.head.length}
                  className="text-center no-results"
                >
                  {searchTerm ||
                  filterStatus !== "all" ||
                  filterGroup !== "all" ? (
                    <div>
                      <FontAwesomeIcon
                        icon={["fas", "search"]}
                        className="no-results-icon"
                      />
                      <p>No brands found matching your search criteria</p>
                      <button className="btn btn-link" onClick={clearFilters}>
                        Clear filters to see all brands
                      </button>
                    </div>
                  ) : (
                    <div>
                      <FontAwesomeIcon
                        icon={["fas", "tag"]}
                        className="no-results-icon"
                      />
                      <p>No brands available</p>
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const valueChange = async (group, brand) => {
    let groupIndex = group.brands
      .map((brands, index) => ({ ...brands, index: index }))
      .find((brands) => brands.braKey === brand.braKey);

    const newStatus = group.brands[groupIndex.index].status === 1 ? 0 : 1;
    group.brands[groupIndex.index].status = newStatus;

    let dataToUpdate = {
      ...group,
    };

    try {
      await axios.put(
        props.chimeraserv.urlPost + "/group/update",
        dataToUpdate,
        {
          headers: {
            Authorization: props.user.data.usrKey,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      // Update the local state to reflect the change
      setTable((prev) => ({
        ...prev,
        data: prev.data.map((item) =>
          item._id === group._id
            ? {
                ...item,
                brands: item.brands.map((b) =>
                  b.braKey === brand.braKey ? { ...b, status: newStatus } : b
                ),
              }
            : item
        ),
      }));
    } catch (error) {
      console.error("Error updating brand status:", error);
      // Revert on error (optional)
      setTable((prev) => ({
        ...prev,
        data: prev.data.map((item) =>
          item._id === group._id
            ? {
                ...item,
                brands: item.brands.map((b) =>
                  b.braKey === brand.braKey ? { ...b, status: brand.status } : b
                ),
              }
            : item
        ),
      }));
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilter = (e) => {
    setFilterStatus(e.target.value);
  };

  const handleGroupFilter = (e) => {
    setFilterGroup(e.target.value);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
    setFilterGroup("all");
    setSortBy("brand");
    setSortOrder("asc");
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return "sort";
    return sortOrder === "asc" ? "sort-up" : "sort-down";
  };

  const renderData = () =>
    filteredAndSortedData.map((brand, key) => (
      <tr key={`${brand.groupId}-${brand.braKey}`}>
        <td>
          <div className="group-info">
            <strong>{brand.groupName}</strong>
          </div>
        </td>
        <td>
          <div className="brand-info">
            <strong>{brand.name}</strong>
          </div>
        </td>
        <td>
          <span className="count-badge">{brand.chains.length}</span>
        </td>
        <td className="actions">
          {props.user.data.level === 0 ? (
            <div className="actions actionsAdmin">
              <label className="switch">
                <input
                  type="checkbox"
                  id={brand.braKey}
                  checked={!!brand.status}
                  onChange={() => valueChange(brand.group, brand)}
                />
                <span className="slider"></span>
              </label>
              <Link
                to={
                  location.pathname.split("/").splice(0, 4).join("/") +
                  "/edit/" +
                  brand.braKey
                }
                state={{ brandData: brand, groupData: brand.group }}
              >
                <FontAwesomeIcon className="icon" icon={["fas", "cog"]} /> Edit
              </Link>
            </div>
          ) : (
            <div />
          )}
          <Link
            to={
              location.pathname.split("/").splice(0, 4).join("/") +
              "/view/" +
              brand.braKey
            }
            state={{ brandData: brand, groupData: brand.group }}
          >
            <FontAwesomeIcon className="icon" icon={["fas", "eye"]} /> View
          </Link>
        </td>
      </tr>
    ));

  const Loader =
    props.components && props.components.setting && props.components.setting[1]
      ? props.components.setting[1].component
      : () => <div>Loading...</div>;

  return (
    <div className="custForm" id="exportPdf_BrandList">
      <div className="viewTitle">List of Brands</div>

      {/* Search and Filter Controls */}
      <div className="table-controls">
        <div className="search-section">
          <div className="search-input-group">
            <FontAwesomeIcon className="search-icon" icon={["fas", "search"]} />
            <input
              type="text"
              className="form-control search-input"
              placeholder="Search by brand name, group name, or brand key..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            {searchTerm && (
              <button
                type="button"
                className="clear-search"
                onClick={() => setSearchTerm("")}
                title="Clear search"
              >
                <FontAwesomeIcon icon={["fas", "times"]} />
              </button>
            )}
          </div>
        </div>

        <div className="filters-section">
          <select
            className="form-control filter-select"
            value={filterGroup}
            onChange={handleGroupFilter}
          >
            <option value="all">All Groups</option>
            {availableGroups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>

          <select
            className="form-control filter-select"
            value={filterStatus}
            onChange={handleStatusFilter}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <button
            type="button"
            className="btn btn-secondary clear-filters"
            onClick={clearFilters}
            title="Clear all filters"
          >
            <FontAwesomeIcon icon={["fas", "refresh"]} /> Clear
          </button>
        </div>
      </div>

      {/* Results Summary */}
      {table.data && (
        <div className="results-summary">
          Showing {filteredAndSortedData.length} of {flatBrandData.length}{" "}
          brands
          {(searchTerm || filterStatus !== "all" || filterGroup !== "all") && (
            <span className="filtered-indicator">
              <FontAwesomeIcon icon={["fas", "filter"]} /> Filtered
            </span>
          )}
        </div>
      )}

      <div>
        {!table.data ? (
          <Loader />
        ) : (
          <Window
            user={props.user}
            chimeraserv={props.chimeraserv}
            title={"Brands"}
          >
            <BrandListContent />
          </Window>
        )}
      </div>
    </div>
  );
}

export default ListBrand;
